import type { AxiosInstance } from "axios";

import { API_ROUTES } from "../../../api/ApiRoutes";

/**
 * Which experience a state's admins see at signup (PENG-78). Values mirror
 * the mobile app's `experiences_config` keys. Not stored on the GET payload —
 * derived from each experience's `currencies` arrays by `deriveStateMode`.
 */
export type PredictionOrSweepsMode = "prediction" | "sweepstakes";

export interface StateSweepstakesExperience {
  sportsbook: boolean;
  casino: boolean;
  currencies: string[];
  rips: boolean;
}

export interface StatePredictionExperience {
  sports: boolean;
  culture: boolean;
  minimum_signup_age: number;
  currencies: string[];
  rips: boolean;
}

export interface StateExperiences {
  sweepstakes: StateSweepstakesExperience;
  prediction: StatePredictionExperience;
}

/**
 * Per-US-state experience config as consumed by the State Configuration table.
 * Normalized from GET `/admin-panel/settings/v1/experiences-config`, which
 * returns a map keyed by state code (see `ExperiencesConfigApiEntry`).
 *
 * `mode` is derived from currency arrays (sweeps wins when both kinds are
 * present). `discover` / `rip_packs` are PAM edit-modal leftovers — Discover
 * is not on the payload (always false); `rip_packs` mirrors either experience
 * `rips` for the single modal toggle until that UI is split.
 */
export interface StateExperienceConfig {
  /** 2-letter USA state code — see `usaStates` in `utils/MapStateName.js`. */
  state: string;
  mode: PredictionOrSweepsMode;
  experiences: StateExperiences;
  discover: boolean;
  rip_packs: boolean;
}

export type StateExperienceConfigUpdate = {
  experiences: StateExperiences;
};

/** Wire body for PATCH `/experiences-config/{state}`. */
export type ExperiencesConfigPatchBody = {
  experiences: {
    prediction: {
      minimum_signup_age: string;
      sports: boolean;
      culture: boolean;
      currencies: string[];
      rips: boolean;
    };
    sweepstakes: {
      casino: boolean;
      sportsbook: boolean;
      currencies: string[];
      rips: boolean;
    };
  };
};

export function toExperiencesConfigPatchBody(
  experiences: StateExperiences,
): ExperiencesConfigPatchBody {
  return {
    experiences: {
      prediction: {
        minimum_signup_age: String(experiences.prediction.minimum_signup_age),
        sports: experiences.prediction.sports,
        culture: experiences.prediction.culture,
        currencies: experiences.prediction.currencies,
        rips: experiences.prediction.rips,
      },
      sweepstakes: {
        casino: experiences.sweepstakes.casino,
        sportsbook: experiences.sweepstakes.sportsbook,
        currencies: experiences.sweepstakes.currencies,
        rips: experiences.sweepstakes.rips,
      },
    },
  };
}

/** Wire shape of one state under GET `/experiences-config`. */
export interface ExperiencesConfigApiEntry {
  experiences: {
    prediction: {
      culture: boolean;
      currencies?: string[];
      /** API may send this as a string (`"21"`) or number. */
      minimum_signup_age: number | string;
      rips?: boolean;
      sports: boolean;
    };
    sweepstakes: {
      casino: boolean;
      currencies?: string[];
      rips?: boolean;
      sportsbook: boolean;
    };
  };
}

export type ExperiencesConfigApiResponse = Record<
  string,
  ExperiencesConfigApiEntry
>;

const DEFAULT_MIN_SIGNUP_AGE = 18;
const SWEEPS_CURRENCY_KEYS = new Set(["rebet_cash", "rebet_coin"]);
const PREDICTION_CURRENCY_KEYS = new Set(["prediction_cash"]);

function hasAnyCurrency(currencies: string[] | undefined, keys: Set<string>) {
  return (currencies ?? []).some((c) => keys.has(c));
}

/**
 * Prediction/Sweepstakes mode from currency arrays. Sweeps wins when both
 * kinds are on (same precedence as the PENG-78 currency-section work). A
 * state with no recognized currency falls back to Prediction so the column
 * always renders one of the two labels.
 */
export function deriveStateMode(
  entry: ExperiencesConfigApiEntry,
): PredictionOrSweepsMode {
  const sweepsCurrencies = entry.experiences?.sweepstakes?.currencies;
  const predictionCurrencies = entry.experiences?.prediction?.currencies;
  if (hasAnyCurrency(sweepsCurrencies, SWEEPS_CURRENCY_KEYS)) {
    return "sweepstakes";
  }
  if (hasAnyCurrency(predictionCurrencies, PREDICTION_CURRENCY_KEYS)) {
    return "prediction";
  }
  return "prediction";
}

export function normalizeExperiencesConfigEntry(
  state: string,
  entry: ExperiencesConfigApiEntry,
): StateExperienceConfig {
  const prediction = entry.experiences?.prediction;
  const sweepstakes = entry.experiences?.sweepstakes;
  const signupAge = Number(prediction?.minimum_signup_age);
  const predictionRips = !!prediction?.rips;
  const sweepstakesRips = !!sweepstakes?.rips;

  return {
    state,
    mode: deriveStateMode(entry),
    experiences: {
      sweepstakes: {
        sportsbook: !!sweepstakes?.sportsbook,
        casino: !!sweepstakes?.casino,
        currencies: Array.isArray(sweepstakes?.currencies)
          ? sweepstakes.currencies
          : [],
        rips: sweepstakesRips,
      },
      prediction: {
        sports: !!prediction?.sports,
        culture: !!prediction?.culture,
        currencies: Array.isArray(prediction?.currencies)
          ? prediction.currencies
          : [],
        rips: predictionRips,
        minimum_signup_age:
          Number.isFinite(signupAge) && signupAge > 0
            ? signupAge
            : DEFAULT_MIN_SIGNUP_AGE,
      },
    },
    discover: false,
    rip_packs: predictionRips || sweepstakesRips,
  };
}

function isExperiencesConfigEntry(
  value: unknown,
): value is ExperiencesConfigApiEntry {
  if (!value || typeof value !== "object") return false;
  const experiences = (value as ExperiencesConfigApiEntry).experiences;
  return !!experiences && typeof experiences === "object";
}

/**
 * Accepts the live map payload, an envelope-wrapped map (`{ data: map }`),
 * or a pre-normalized array (tests / transitional backends).
 */
export function parseExperiencesConfigPayload(
  data: unknown,
): StateExperienceConfig[] {
  const payload =
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    "data" in data &&
    (data as { data: unknown }).data !== undefined
      ? (data as { data: unknown }).data
      : data;

  if (Array.isArray(payload)) {
    return payload.filter(
      (row): row is StateExperienceConfig =>
        !!row && typeof row === "object" && typeof row.state === "string",
    );
  }

  if (!payload || typeof payload !== "object") return [];

  return Object.entries(payload as Record<string, unknown>)
    .filter(([, value]) => isExperiencesConfigEntry(value))
    .map(([state, value]) =>
      normalizeExperiencesConfigEntry(state, value as ExperiencesConfigApiEntry),
    )
    .sort((a, b) => a.state.localeCompare(b.state));
}

/** GET /admin-panel/settings/v1/experiences-config — full per-state config map. */
export async function fetchStateConfigurations(
  client: AxiosInstance,
): Promise<StateExperienceConfig[]> {
  const response = await client.get(API_ROUTES.GET_STATE_CONFIGS);
  return parseExperiencesConfigPayload(response?.data);
}

/** PATCH /admin-panel/settings/v1/experiences-config/{state} — update one state. */
export async function updateStateConfiguration(
  client: AxiosInstance,
  state: string,
  update: StateExperienceConfigUpdate,
): Promise<StateExperienceConfig> {
  const url = API_ROUTES.UPDATE_STATE_CONFIG.replace(
    "{state}",
    encodeURIComponent(state),
  );
  const body = toExperiencesConfigPatchBody(update.experiences);
  const response = await client.patch(url, body);
  const payload = response?.data?.data ?? response?.data;

  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    if (isExperiencesConfigEntry(payload)) {
      return normalizeExperiencesConfigEntry(state, payload);
    }
    const keyed = (payload as Record<string, unknown>)[state];
    if (isExperiencesConfigEntry(keyed)) {
      return normalizeExperiencesConfigEntry(state, keyed);
    }
  }

  return normalizeExperiencesConfigEntry(state, body);
}

/**
 * Whether any product experience is switched on for a state. There is no
 * dedicated "active" flag — kept for the edit-modal / legacy helpers.
 */
export function isStateConfigActive(config: StateExperienceConfig): boolean {
  return (
    config.experiences.sweepstakes.sportsbook ||
    config.experiences.sweepstakes.casino ||
    config.experiences.prediction.culture ||
    config.experiences.prediction.sports ||
    config.experiences.prediction.rips ||
    config.experiences.sweepstakes.rips ||
    config.discover ||
    config.rip_packs
  );
}
